import React, {
  useEffect,
  useState,
  useCallback,
  SyntheticEvent,
  useMemo,
  useRef,
  RefObject,
} from 'react';
import PatientSearchIcon from '@mui/icons-material/PersonSearchRounded';
import AddContactIcon from '@mui/icons-material/PersonAdd';
import Divider from '@mui/material/Divider';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Dialog from 'pages/Dashboard/components/Dialog';
import isNil from 'lodash/isNil';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import Button from 'pages/Dashboard/components/Button';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import noop from 'lodash/noop';
import {
  FaxContactDTO,
  FaxContactDTOPaginatedResponseDTO,
  MultiMediaResponse,
  SendFaxDTO,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import {
  useContacts,
  useSendFax,
} from 'pages/Dashboard/pages/Fax/hooks';
import Input from 'pages/Dashboard/components/Input';
import ToolTip from 'core/components/Tooltip';
import {
  faxEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import IconButton from '@mui/material/IconButton';
import Empty from 'core/components/Empty';
import clsx from 'clsx';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import debounce from 'lodash/debounce';
import ContactLookup from 'pages/Dashboard/pages/Fax/ContactLookup';
import AddContactsForm, {
  AddContactFormParams,
} from 'pages/Dashboard/pages/Contacts/ContactForm';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  getErrorMessage,
} from 'utils/errors';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import FollowUpToDo, {
  isFollowUpSelection,
} from 'pages/Dashboard/pages/Fax/FollowUpToDo';
import {
  FaxCreateData,
} from 'pages/Dashboard/pages/Fax/types';
import PatientMediaSelect from 'views/EMR/components/PatientMediaSelect';

type FaxDialogProps = {
  open: boolean;
  onClose: () => void;
  patient?: PatientCompact;
  contact?: FaxContactDTO;
  patientMultimediaIds?: number[];
  attachFaceSheet?: boolean;
  shouldCreateFollowUpTodo?: boolean;
};

// These are the max-lengths allowed by documo
// https://docs.documo.com/#4c501802-7d0b-4e70-98ef-64249d3606fb
const FAX_FIELD_LIMITS = {
  recipientName: 40,
  senderName: 40,
  subject: 55,
  message: 4000,
} as const;

export default function SendFax({
  open,
  onClose,
  patient: defaultPatient,
  contact: defaultContact,
  patientMultimediaIds: defaultPatientMultimediaIds,
  attachFaceSheet: defaultAttachFaceSheet,
  shouldCreateFollowUpTodo: defaultShouldCreateFollowUpTodo = false,
}: FaxDialogProps) {
  const {
    mutateAsync: sendFax,
  } = useSendFax();
  const userInfo = useUserInfo();
  const notify = useNotify();
  const [contactsOpen, setContactsOpen] = useState<boolean>(false);
  const [addContactFormParams, setAddContactFormParams] = useState<
    AddContactFormParams | null>(null);
  const [page, setPage] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [hasQueryChanged, setHasQueryChanged] = useState<boolean>(false);

  const {
    data: contacts = {} as FaxContactDTOPaginatedResponseDTO,
    refetch: refetchContacts,
    isLoading,
    isFetching,
  } = useContacts(
    searchText,
    0,
    (page + 1) * 25,
    open && hasQueryChanged,
  );

  const handleInputChange = useCallback(debounce((event: SyntheticEvent, value: string) => {
    if (contactsOpen) {
      setSearchText(value);
      if (!isEmptyString(value)) {
        setPage(0);
        setHasQueryChanged(true);
      }
    }
  }, 700), [contactsOpen]);

  const formProps = useForm<FaxCreateData>({
    mode: 'onChange',
    defaultValues: {
      recipient: defaultContact ?? undefined,
      senderName: getDisplayName(userInfo?.user?.formattedNames),
      patient: defaultPatient,
      subject: '',
      message: '',
      attachFaceSheet: defaultAttachFaceSheet ?? true,
      patientMultimediaIds: defaultPatientMultimediaIds ?? [],
      shouldCreateFollowUpTodo: defaultShouldCreateFollowUpTodo,
      assignee: null,
    },
  });
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = formProps;

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FaxCreateData) => {
    const {
      message,
      patient,
      recipient,
      senderName,
      subject,
      attachFaceSheet,
      patientMultimediaIds,
      shouldCreateFollowUpTodo,
      assignee,
    } = data;
    const payload: SendFaxDTO = {
      recipientNumber: recipient?.faxNumber ?? '',
      recipientName: recipient?.name ?? '',
      senderName,
      message,
      subject,
      patientId: patient?.patientId,
      patientMultiMediaIds: patientMultimediaIds,
      attachFaceSheet,
      toDoContext: shouldCreateFollowUpTodo
        ? { assigneeUserId: assignee?.userId }
        : undefined,
    };
    try {
      await sendFax(payload);
    } catch (error) {
      reset(data);
      notify({
        variant: 'error',
        message: getErrorMessage(error) ?? 'Fax Send Failed',
      });
      return;
    }

    notify({
      variant: 'success',
      message: 'Fax Sent Successfully',
    });
    handleClose();
    faxEventChannel.emit('onFaxChange');
  };

  const [patient, attachFaceSheet] = watch([
    'patient',
    'attachFaceSheet',
  ]);
  useEffect(() => {
    const unsubscribeFromChange = faxEventChannel.on('onContactChange', () => {
      refetchContacts();
    });

    return () => {
      unsubscribeFromChange();
    };
  }, [refetchContacts]);

  const hasPatient = !isNil(patient);

  const isDisabled = !isValid || isSubmitting;

  const searchTextPhoneNumber = useMemo(() => {
    const maybePhoneNumber = searchText.replace(/\+|\(|\)|-|\.|\s/gm, '');
    return /^\d{10,}$/.test(maybePhoneNumber) ? maybePhoneNumber : '';
  }, [searchText]);

  const dialogRef = useRef<HTMLDivElement>(undefined) as RefObject<HTMLDivElement>;
  const [isFollowUpEligible, setIsFollowUpEligible] = useState(defaultShouldCreateFollowUpTodo);

  const handleSelectedMediaChange = useCallback((mediaList: MultiMediaResponse[]) => {
    const isEligible = isFollowUpSelection(mediaList);
    setIsFollowUpEligible(isEligible);
    if (isEligible) {
      if (!isFollowUpEligible) {
        setValue('shouldCreateFollowUpTodo', true);
      }
    } else {
      setValue('shouldCreateFollowUpTodo', false, { shouldValidate: true });
      setValue('assignee', null, { shouldValidate: true });
    }
  }, [isFollowUpEligible, setValue]);

  return (
    <FormProvider {...formProps}>
      <Dialog
        open={open}
        title="Send Fax"
        onClose={() => {
          if (!isSubmitting) {
            handleClose();
          }
        }}
        autoFocusOptions={{
          enabled: true,
          inputSelector: defaultContact ? '#fax-subject' : '#contact',
          skipIfValueExists: false,
        }}
        onConfirm={noop}
        confirmText="Send"
        className="md:w-[44rem] lg:!w-[64rem] xl:!w-[80rem] transition-all duration-300 !space-y-0 overflow-y-hidden !p-0"
        titleClassName="p-5"
        dialogRef={dialogRef}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-form-row overflow-y-auto px-5 text-xs text-gray-500 lg:flex-row lg:gap-8">
          <div className="flex shrink-0 flex-col gap-form-row w-full lg:w-[300px] xl:w-[400px]">
            <div className="flex w-full flex-col gap-2 justify-center h-[52px]">
              <div className="flex gap-form-field items-end">
                <Controller
                  name="recipient"
                  control={control}
                  rules={{
                    required: 'Recipient is required',
                    maxLength: FAX_FIELD_LIMITS.recipientName,
                  }}
                  render={({ field, fieldState: { invalid } }) => (
                    <>
                      <ContactLookup
                        {...field}
                        isLoading={isLoading}
                        error={invalid}
                        isSubmitting={isSubmitting}
                        handleInputChange={handleInputChange}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        isSearching={isFetching}
                        contacts={contacts?.data ?? []}
                        pageProps={contacts?.pageProperties ?? {}}
                        handleOpen={() => { setContactsOpen(true); }}
                        handleClose={() => { setContactsOpen(false); }}
                        open={contactsOpen}
                        page={page}
                        setPage={setPage}
                        isFetching={isFetching}
                      />
                      <ToolTip
                        title="Add Contact"
                        aria-label="Add Contact"
                      >
                        <span>
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => {
                              setAddContactFormParams({
                                defaultPhoneNumber: searchTextPhoneNumber,
                              });
                            }}
                            disabled={isSubmitting}
                          >
                            <AddContactIcon
                              className={clsx('!w-5 !h-5', { 'text-blue-500': !isSubmitting })}
                            />
                          </IconButton>
                        </span>
                      </ToolTip>
                    </>
                  )}
                />
              </div>
            </div>
            <Controller
              name="senderName"
              control={control}
              rules={{
                required: 'Sender name is required',
                maxLength: FAX_FIELD_LIMITS.senderName,
              }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  error={invalid}
                  showErrorText={false}
                  required
                  labelClasses="font-semibold text-xs text-gray-500"
                  label="Sender Name"
                  placeholder="Fax Sender Name"
                  id="sender-name"
                  trackingLabel="Fax Sender Name"
                  disabled={isSubmitting}
                  inputProps={{ maxLength: FAX_FIELD_LIMITS.senderName }}
                  allowSpeechToText
                />
              )}
            />
            <Controller
              name="subject"
              control={control}
              rules={{
                required: 'Subject is required',
                maxLength: FAX_FIELD_LIMITS.subject,
              }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  error={invalid}
                  showErrorText={false}
                  required
                  labelClasses="font-semibold text-xs text-gray-500"
                  label="Subject"
                  allowSpeechToText
                  placeholder="Enter Fax Subject here"
                  inputProps={{ maxLength: 55 }}
                  id="fax-subject"
                  trackingLabel="Fax Subject"
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name="message"
              control={control}
              rules={{
                maxLength: FAX_FIELD_LIMITS.message,
              }}
              render={({ field, fieldState: { invalid } }) => (
                <TextAreaWithActions
                  {...field}
                  error={invalid}
                  label="Message"
                  id="fax-message"
                  placeholder="Enter message here..."
                  maxLength={FAX_FIELD_LIMITS.message}
                  rows={12}
                  maxRows={12}
                  trackingLabel="Create To Do Comments"
                  disabled={isSubmitting}
                />
              )}
            />
          </div>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className="hidden lg:flex"
          />
          <Divider className="lg:hidden" />
          <div className="flex flex-col gap-form-row flex-1">
            <div className="w-full flex flex-col gap-2">
              <Controller
                control={control}
                name="patient"
                rules={{ required: 'Patient is required' }}
                render={({ field, fieldState: { invalid } }) => (
                  <PatientSelect
                    {...field}
                    error={invalid}
                    id="patient-lookup"
                    label="Patient"
                    placeholder="Enter patient name, phone or date of birth"
                    allowDefault
                    value={patient}
                    required
                    classes={{ input: 'h-8', root: '!mb-0' }}
                    readonly={isSubmitting}
                    onChange={(nextPatient) => {
                      field.onChange(nextPatient);
                      setValue('patientMultimediaIds', null);
                      setValue('shouldCreateFollowUpTodo', false);
                      setValue('assignee', null, { shouldValidate: true });
                    }}
                  />
                )}
              />
              {hasPatient && (
                <Controller
                  control={control}
                  name="attachFaceSheet"
                  render={({ field: { value: checked, ...field } }) => (
                    <Checkbox
                      {...field}
                      checked={checked}
                      id="attach-face-sheet"
                      className={checked ? '!text-blue-500' : ''}
                      label={(
                        <div className="font-semibold text-gray-500">
                          Include Patient&apos;s Face Sheet
                        </div>
                      )}
                    />
                  )}
                />
              )}
            </div>
            {hasPatient ? (
              <div className="flex flex-col gap-0">
                <Controller
                  control={control}
                  rules={{ required: { value: !attachFaceSheet, message: 'At least 1 attachment required' } }}
                  name="patientMultimediaIds"
                  render={({ field }) => (
                    <PatientMediaSelect
                      {...field}
                      patientId={patient?.patientId ?? 0}
                      offsetHeight={dialogRef.current?.offsetHeight ?? 0}
                      disabled={false}
                      className="!h-auto min-h-0 flex-1"
                      classes={{ table: clsx({
                        '!max-h-[360px]': !isFollowUpEligible,
                        '!max-h-[300px]': isFollowUpEligible,
                      }) }}
                      onSelectedMediaChange={handleSelectedMediaChange}
                    />
                  )}
                />
                <FollowUpToDo
                  isEligible={isFollowUpEligible}
                  isSubmitting={isSubmitting}
                />
              </div>
            ) : (
              <Empty
                iconSlot={
                  <PatientSearchIcon className="!w-8 !h-8 !text-inherit" />
                }
              >
                <div className="text-sm font-semibold text-center">
                  Before selecting attachments,
                  <br />
                  you must first select a patient.
                </div>
              </Empty>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-row-reverse items-center justify-between p-5">
          <DisabledFormTooltip>
            <Button
              className="!min-w-[100px]"
              variant="contained"
              color="primary"
              type="submit"
              disabled={isDisabled}
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </Button>
          </DisabledFormTooltip>
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Close
          </Button>
        </div>
      </Dialog>
      {!isNil(addContactFormParams) && (
        <AddContactsForm
          {...addContactFormParams}
          open
          onClose={() => { setAddContactFormParams(null); }}
          onAdd={(recipient) => {
            setValue('recipient', recipient, { shouldValidate: true });
          }}
        />
      )}
    </FormProvider>
  );
}
